import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  History,
  Loader2,
  Search,
  Eye,
  Filter,
  Calendar,
  User,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  ChevronLeft,
  ChevronRight,
  Building2,
  Download,
  Trash2,
  Lock,
  ShieldAlert,
  AlertTriangle
} from "lucide-react";

const ESTADO_CONFIG = {
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  aprobada: { label: "Aprobada", color: "bg-blue-100 text-blue-700 border-blue-200", icon: CheckCircle },
  rechazada: { label: "Rechazada", color: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  parcial: { label: "Entrega Parcial", color: "bg-purple-100 text-purple-700 border-purple-200", icon: Package },
  entregada: { label: "Entregada", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle },
  cancelada: { label: "Cancelada", color: "bg-slate-100 text-slate-700 border-slate-200", icon: XCircle }
};

const PRIORIDAD_CONFIG = {
  baja: { label: "Baja", color: "bg-slate-100 text-slate-600" },
  normal: { label: "Normal", color: "bg-blue-100 text-blue-600" },
  alta: { label: "Alta", color: "bg-orange-100 text-orange-600" },
  urgente: { label: "Urgente", color: "bg-red-100 text-red-600" }
};

export default function HistorialRequisicionesPage() {
  const [requisiciones, setRequisiciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedReq, setSelectedReq] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Bulk selection states
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  
  const pageSize = 25;
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", pageSize);
      params.append("skip", page * pageSize);
      if (estadoFilter !== "all") params.append("estado", estadoFilter);

      const response = await axios.get(`${API}/requisiciones?${params.toString()}`, getAuthHeaders());
      setRequisiciones(response.data.requisiciones || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Error al cargar historial");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, estadoFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleDateString("es", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const viewDetail = async (req) => {
    try {
      const response = await axios.get(`${API}/requisiciones/${req.id}`, getAuthHeaders());
      setSelectedReq(response.data);
      setShowDetailDialog(true);
    } catch (err) {
      toast.error("Error al cargar detalles");
    }
  };

  // Bulk selection functions
  const toggleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === filteredRequisiciones.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredRequisiciones.map(r => r.id));
    }
  };

  const openDeleteDialog = () => {
    if (selectedItems.length === 0) {
      toast.error("No hay requisiciones seleccionadas");
      return;
    }
    setAuthEmail("");
    setAuthPassword("");
    setShowDeleteDialog(true);
  };

  const executeBulkDelete = async () => {
    if (!authEmail.trim() || !authPassword.trim()) {
      toast.error("Ingrese las credenciales de autorización");
      return;
    }
    
    setDeleting(true);
    try {
      // Delete all selected items with authorization
      let successCount = 0;
      let errorCount = 0;
      
      for (const id of selectedItems) {
        try {
          await axios.post(
            `${API}/requisiciones/${id}/delete-with-auth`,
            { email: authEmail, password: authPassword },
            getAuthHeaders()
          );
          successCount++;
        } catch (err) {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} requisición(es) eliminada(s)`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} requisición(es) no pudieron ser eliminadas`);
      }
      
      setShowDeleteDialog(false);
      setSelectedItems([]);
      fetchData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error al eliminar";
      toast.error(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  const filteredRequisiciones = requisiciones.filter(req => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      req.numero?.toLowerCase().includes(search) ||
      req.solicitante_nombre?.toLowerCase().includes(search) ||
      req.motivo?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="historial-requisiciones-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center">
              <History className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Historial de Requisiciones
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Registro histórico de todas las solicitudes
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por número, solicitante, motivo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-48">
                <Select value={estadoFilter} onValueChange={(v) => { setEstadoFilter(v); setPage(0); }}>
                  <SelectTrigger>
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="pendiente">Pendientes</SelectItem>
                    <SelectItem value="aprobada">Aprobadas</SelectItem>
                    <SelectItem value="parcial">Entrega Parcial</SelectItem>
                    <SelectItem value="entregada">Entregadas</SelectItem>
                    <SelectItem value="rechazada">Rechazadas</SelectItem>
                    <SelectItem value="cancelada">Canceladas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        {selectedItems.length > 0 && (
          <Card className="mb-4 border-blue-200 bg-blue-50 dark:bg-blue-900/20">
            <CardContent className="py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedItems.length === filteredRequisiciones.length}
                  onCheckedChange={toggleSelectAll}
                />
                <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  {selectedItems.length} requisición(es) seleccionada(s)
                </span>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={openDeleteDialog}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar Seleccionadas
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <Card>
            <ScrollArea className="h-[calc(100vh-320px)]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedItems.length === filteredRequisiciones.length && filteredRequisiciones.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>Número</TableHead>
                    <TableHead>Solicitante</TableHead>
                    <TableHead>Departamento</TableHead>
                    <TableHead>Fecha Solicitud</TableHead>
                    <TableHead>Prioridad</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Procesado Por</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequisiciones.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-12">
                        <History className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                        <p className="text-slate-500">No se encontraron requisiciones</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRequisiciones.map((req) => {
                      const estadoConfig = ESTADO_CONFIG[req.estado] || ESTADO_CONFIG.pendiente;
                      const prioridadConfig = PRIORIDAD_CONFIG[req.prioridad] || PRIORIDAD_CONFIG.normal;
                      const EstadoIcon = estadoConfig.icon;
                      const isSelected = selectedItems.includes(req.id);
                      
                      return (
                        <TableRow key={req.id} className={isSelected ? "bg-blue-50 dark:bg-blue-900/20" : ""}>
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleSelectItem(req.id)}
                              data-testid={`select-item-${req.id}`}
                            />
                          </TableCell>
                          <TableCell className="font-mono font-medium">{req.numero}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              {req.solicitante_nombre}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              {req.departamento_nombre || "-"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              {formatDate(req.created_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={prioridadConfig.color}>{prioridadConfig.label}</Badge>
                          </TableCell>
                          <TableCell>{req.items?.length || 0}</TableCell>
                          <TableCell>
                            <Badge className={`${estadoConfig.color} flex items-center gap-1 w-fit`}>
                              <EstadoIcon className="w-3 h-3" />
                              {estadoConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {req.aprobador_nombre ? (
                              <div className="text-sm">
                                <p>{req.aprobador_nombre}</p>
                                <p className="text-xs text-slate-500">{formatDate(req.fecha_aprobacion)}</p>
                              </div>
                            ) : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => viewDetail(req)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </ScrollArea>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-slate-500">
                  Mostrando {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} de {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">Página {page + 1} de {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Requisición {selectedReq?.numero}
              </DialogTitle>
            </DialogHeader>
            
            {selectedReq && (
              <div className="space-y-4">
                {/* Info Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500">Solicitante</p>
                    <p className="font-medium">{selectedReq.solicitante_nombre}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Departamento</p>
                    <p className="font-medium">{selectedReq.departamento_nombre || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Fecha Solicitud</p>
                    <p className="font-medium">{formatDate(selectedReq.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Fecha Requerida</p>
                    <p className="font-medium">{formatDate(selectedReq.fecha_requerida)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Prioridad</p>
                    <Badge className={PRIORIDAD_CONFIG[selectedReq.prioridad]?.color}>
                      {PRIORIDAD_CONFIG[selectedReq.prioridad]?.label}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Estado</p>
                    <Badge className={ESTADO_CONFIG[selectedReq.estado]?.color}>
                      {ESTADO_CONFIG[selectedReq.estado]?.label}
                    </Badge>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">Motivo</p>
                    <p className="font-medium text-sm">{selectedReq.motivo || "-"}</p>
                  </div>
                </div>

                {/* Items Table */}
                <div>
                  <p className="text-sm font-medium mb-2">Artículos</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Artículo</TableHead>
                        <TableHead className="text-right">Solicitado</TableHead>
                        <TableHead className="text-right">Aprobado</TableHead>
                        <TableHead className="text-right">Entregado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReq.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-sm">{item.articulo?.codigo}</TableCell>
                          <TableCell>
                            <div>
                              <p>{item.articulo?.nombre}</p>
                              {item.observaciones && (
                                <p className="text-xs text-slate-500">{item.observaciones}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{item.cantidad} {item.articulo?.unidad_medida}</TableCell>
                          <TableCell className="text-right text-blue-600">{item.cantidad_aprobada || 0}</TableCell>
                          <TableCell className="text-right text-green-600">{item.cantidad_entregada || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Processing Info */}
                {selectedReq.aprobador_nombre && (
                  <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                    <p className="text-sm">
                      <span className="text-slate-500">{selectedReq.estado === "rechazada" ? "Rechazado" : "Aprobado"} por:</span>{" "}
                      <span className="font-medium">{selectedReq.aprobador_nombre}</span>
                    </p>
                    <p className="text-sm">
                      <span className="text-slate-500">Fecha:</span>{" "}
                      <span className="font-medium">{formatDate(selectedReq.fecha_aprobacion)}</span>
                    </p>
                    {selectedReq.observaciones_aprobacion && (
                      <p className="text-sm">
                        <span className="text-slate-500">Observaciones:</span>{" "}
                        {selectedReq.observaciones_aprobacion}
                      </p>
                    )}
                  </div>
                )}

                {/* Cancellation Info */}
                {selectedReq.estado === "cancelada" && selectedReq.motivo_cancelacion && (
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700">
                      <span className="font-medium">Motivo de cancelación:</span>{" "}
                      {selectedReq.motivo_cancelacion}
                    </p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Authorization Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <ShieldAlert className="w-5 h-5" />
                Autorización Requerida
              </DialogTitle>
              <DialogDescription>
                Para eliminar {selectedItems.length} requisición(es), ingrese las credenciales de un usuario autorizado.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Acción irreversible
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Solo usuarios con el permiso "Eliminar requisiciones" o administradores pueden autorizar esta acción.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="auth-email">Email del usuario autorizador</Label>
                <Input
                  id="auth-email"
                  type="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  placeholder="admin@empresa.com"
                  data-testid="auth-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="auth-password">Contraseña</Label>
                <Input
                  id="auth-password"
                  type="password"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  placeholder="••••••••"
                  onKeyDown={(e) => e.key === "Enter" && executeBulkDelete()}
                  data-testid="auth-password-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteDialog(false)}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={executeBulkDelete}
                disabled={deleting || !authEmail || !authPassword}
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Autorizar y Eliminar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
